import type {
  KnowledgePageRevision,
  KnowledgeTree,
  PageHistoryEntry,
  PageLinkRef,
  PageProposal,
  PageProposalKind,
  PageProposalStatus,
  PageRecord,
  PageSection,
  PageSuggestion,
  PageView,
  RevisionPeople,
  SuggestionChange,
  SuggestionSource,
  TreePage,
  WriteOutcome,
  WriteResult,
} from './types';

// Every wiki response is checked here; a shape the UI cannot trust becomes a
// visible error instead of a crash or a half-rendered claim.

export class UnexpectedPagesResponse extends Error {
  constructor(what: string) {
    super('Poneva returned an unexpected response. Please try again.');
    this.name = `UnexpectedPagesResponse(${what})`;
  }
}

type Record_ = Record<string, unknown>;
const isRecord = (value: unknown): value is Record_ =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown) => (typeof value === 'string' && value ? value : null);
const count = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
const list = (value: unknown) => (Array.isArray(value) ? value : []);
function required(value: unknown, what: string) {
  const found = text(value);
  if (!found) throw new UnexpectedPagesResponse(what);
  return found;
}
function record(value: unknown, what: string) {
  if (!isRecord(value)) throw new UnexpectedPagesResponse(what);
  return value;
}
function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return allowed.includes(value as T) ? (value as T) : null;
}

const authorKinds = ['user', 'assistant', 'external_agent', 'import'] as const;
const proposalKinds = ['create_page', 'update_page', 'archive_page', 'move_page'] as const;
const proposalStatuses = ['pending', 'approved', 'rejected', 'withdrawn', 'superseded'] as const;
const outcomes = [
  'saved',
  'created',
  'moved',
  'archived',
  'restored',
  'deleted',
  'unchanged',
  'suggested',
] as const;

function parseTreePage(value: unknown): TreePage {
  const item = record(value, 'tree page');
  return {
    id: required(item.id, 'tree page id'),
    title: text(item.title) ?? 'Untitled page',
    parentId: text(item.parentId),
    position: count(item.position) ?? 0,
    isRoot: item.isRoot === true,
    archivedAt: text(item.archivedAt),
    currentRevisionId: text(item.currentRevisionId),
    currentRevisionNumber: count(item.currentRevisionNumber),
    updatedAt: text(item.updatedAt),
    openSuggestions: count(item.openSuggestions) ?? 0,
  };
}

export function parseTree(value: unknown): KnowledgeTree {
  const body = record(value, 'tree');
  if (!Array.isArray(body.pages)) throw new UnexpectedPagesResponse('tree pages');
  const rootId = required(body.rootId, 'tree root');
  const pages = body.pages.map(parseTreePage);
  if (!pages.some((page) => page.id === rootId)) throw new UnexpectedPagesResponse('tree root');
  return { rootId, pages, canPublish: body.canPublish === true };
}

function parseSection(value: unknown): PageSection | null {
  if (!isRecord(value)) return null;
  const id = text(value.id);
  const start = count(value.start);
  const end = count(value.end);
  if (!id || start === null || end === null || end < start) return null;
  return {
    id,
    title: typeof value.title === 'string' ? value.title : '',
    level: count(value.level) ?? 0,
    path: list(value.path).filter((part): part is string => typeof part === 'string'),
    start,
    end,
  };
}

/** Who wrote, accepted or edited a revision; unknown people are null, never guessed. */
function parsePeople(item: Record_): RevisionPeople {
  return {
    authorKind: oneOf(item.authorKind, authorKinds),
    authorName: text(item.authorName),
    clientName: text(item.clientName),
    approvedByName: text(item.approvedByName),
    editedByName: text(item.editedByName),
    proposalId: text(item.proposalId),
  };
}

export function parseRevision(value: unknown): KnowledgePageRevision {
  const item = record(value, 'revision');
  const number = count(item.number);
  if (!number || typeof item.markdown !== 'string') throw new UnexpectedPagesResponse('revision');
  return {
    id: required(item.id, 'revision id'),
    number,
    title: text(item.title) ?? '',
    markdown: item.markdown,
    sections: list(item.sections)
      .map(parseSection)
      .filter((section): section is PageSection => section !== null),
    note: text(item.note),
    ...parsePeople(item),
    createdAt: text(item.createdAt),
  };
}

function parsePageRecord(value: unknown): PageRecord {
  const page = record(value, 'page');
  return {
    id: required(page.id, 'page id'),
    title: text(page.title) ?? 'Untitled page',
    parentId: text(page.parentId),
    isRoot: page.isRoot === true,
    currentRevisionId: text(page.currentRevisionId),
    currentRevisionNumber: count(page.currentRevisionNumber),
    archivedAt: text(page.archivedAt),
    updatedAt: text(page.updatedAt),
  };
}

function parseLinkRef(value: unknown): PageLinkRef | null {
  if (!isRecord(value)) return null;
  const id = text(value.id);
  return id ? { id, title: text(value.title) ?? 'Untitled page' } : null;
}
const linkRefs = (value: unknown) =>
  list(value)
    .map(parseLinkRef)
    .filter((ref): ref is PageLinkRef => ref !== null);

function parseChange(value: unknown): SuggestionChange | null {
  if (!isRecord(value)) return null;
  const start = count(value.start);
  const end = count(value.end);
  if (start === null || end === null || end < start) return null;
  return {
    start,
    end,
    deleted: typeof value.deleted === 'string' ? value.deleted : '',
    inserted: typeof value.inserted === 'string' ? value.inserted : '',
  };
}

/**
 * Where a suggestion came from. A chat names its thread; a meeting names its
 * id and title (or only its title when this viewer cannot open it). Anything
 * else is left out rather than guessed.
 */
export function parseSource(value: unknown): SuggestionSource | null {
  if (!isRecord(value)) return null;
  const url = text(value.url);
  const meetingTitle = text(value.title) ?? text(value.meetingTitle);
  if (text(value.meetingId) || meetingTitle) return { kind: 'meeting', title: meetingTitle, url };
  if (url) return { kind: 'chat', url };
  return null;
}

function parseSuggestion(value: unknown): PageSuggestion {
  const item = record(value, 'suggestion');
  const kind = oneOf<PageProposalKind>(item.kind, proposalKinds);
  if (!kind) throw new UnexpectedPagesResponse('suggestion kind');
  const state = item.state === 'outdated' ? 'outdated' : 'open';
  return {
    id: required(item.id, 'suggestion id'),
    kind,
    state,
    changes:
      state === 'open'
        ? list(item.changes)
            .map(parseChange)
            .filter((change): change is SuggestionChange => change !== null)
        : [],
    title: text(item.title) ?? 'Untitled page',
    rename: text(item.rename),
    parentId: text(item.parentId),
    markdown: typeof item.markdown === 'string' ? item.markdown : null,
    reason: text(item.reason) ?? '',
    authorKind: oneOf(item.authorKind, authorKinds),
    authorName: text(item.authorName),
    clientName: text(item.clientName),
    isMine: item.isMine === true,
    source: parseSource(item.source),
    baseRevisionId: text(item.baseRevisionId),
    createdAt: text(item.createdAt),
  };
}

export function parsePageView(value: unknown): PageView {
  const body = record(value, 'page view');
  return {
    page: parsePageRecord(body.page),
    revision:
      body.revision === null || body.revision === undefined ? null : parseRevision(body.revision),
    children: linkRefs(body.children),
    backlinks: linkRefs(body.backlinks),
    suggestions: list(body.suggestions).map(parseSuggestion),
    canPublish: body.canPublish === true,
  };
}

/** GET /pages/:id?revision=…: one exact revision of a page. */
export function parsePageRevision(value: unknown): KnowledgePageRevision {
  return parseRevision(record(value, 'page revision').revision);
}

/** A write answers with its outcome, the page and revision, or the suggestion it made. */
export function parseWrite(value: unknown): WriteResult {
  const body = record(value, 'write');
  const outcome = oneOf<WriteOutcome>(body.outcome, outcomes);
  if (!outcome) throw new UnexpectedPagesResponse('write outcome');
  const page = isRecord(body.page) ? body.page : {};
  const revision = isRecord(body.revision) ? body.revision : {};
  const suggestion = isRecord(body.suggestion) ? body.suggestion : {};
  if (outcome === 'suggested' && !text(suggestion.id))
    throw new UnexpectedPagesResponse('write suggestion');
  return {
    outcome,
    pageId: text(page.id),
    revisionId: text(revision.id),
    revisionNumber: count(revision.number) || null,
    suggestionId: text(suggestion.id),
  };
}

/** POST /pages/:id/preview: the current text and what suggested edits make of it. */
export function parsePreview(value: unknown) {
  const item = record(value, 'edit preview');
  if (typeof item.before !== 'string' || typeof item.after !== 'string')
    throw new UnexpectedPagesResponse('edit preview text');
  return { before: item.before, after: item.after };
}

/** What an accept did: the new revision (none for an archive) and whether the page is archived. */
export function parseAccept(value: unknown) {
  const body = record(value, 'accept');
  const revision = isRecord(body.revision) ? body.revision : {};
  return {
    revisionNumber: count(revision.number) || null,
    archived: body.archived === true,
  };
}

export function parseHistory(value: unknown): PageHistoryEntry[] {
  const body = record(value, 'history');
  if (!Array.isArray(body.revisions)) throw new UnexpectedPagesResponse('history');
  return body.revisions
    .map((entry) => {
      const item = record(entry, 'history entry');
      const number = count(item.number);
      if (!number) throw new UnexpectedPagesResponse('history number');
      return {
        id: required(item.id, 'history id'),
        number,
        title: text(item.title) ?? '',
        note: text(item.note),
        ...parsePeople(item),
        createdAt: text(item.createdAt),
        characters: count(item.characters),
      };
    })
    .sort((a, b) => b.number - a.number);
}

export function parseProposal(value: unknown): PageProposal {
  const item = record(value, 'proposal');
  const kind = oneOf<PageProposalKind>(item.kind, proposalKinds);
  if (!kind) throw new UnexpectedPagesResponse('proposal kind');
  const pageTitle = text(item.pageTitle);
  return {
    id: required(item.id, 'proposal id'),
    kind,
    status: oneOf<PageProposalStatus>(item.status, proposalStatuses) ?? 'pending',
    pageId: text(item.pageId),
    parentId: text(item.parentId),
    title: text(item.title) ?? pageTitle ?? 'Untitled page',
    pageTitle,
    reason: text(item.reason) ?? '',
    authorKind: oneOf(item.authorKind, authorKinds),
    createdByName: text(item.createdByName),
    clientName: text(item.clientName),
    isMine: item.isMine === true,
    createdAt: text(item.createdAt),
  };
}

export function parseProposalList(value: unknown): PageProposal[] {
  const body = record(value, 'proposal list');
  if (!Array.isArray(body.proposals)) throw new UnexpectedPagesResponse('proposal list');
  return body.proposals.map(parseProposal);
}

/** GET /page-proposals/:id: the proposal itself; the review diff it also carries is not used. */
export function parseProposalDetail(value: unknown): PageProposal {
  return parseProposal(record(value, 'proposal detail').proposal);
}
